import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

// There's more target attribute values, but we only use one anyway.
export type LinkTarget = '_blank' | undefined;
export type LinkColor = 'inherit' | 'light' | 'default';
export type LinkHoverColor = 'inherit' | 'default' | 'light';
export type LinkUnderline = 'always' | 'never' | 'hover';
export type LinkWeight = 'inherit' | 'bold';

@Component({
  selector: 'fl-link',
  template: `
    <a *ngIf="link"
      class="LinkElement"
      [routerLink]="link"
      [queryParams]="queryParams"
      [target]="linkTarget"
      [rel]="allRel"
    >
      <ng-container [ngTemplateOutlet]="content"></ng-container>
    </a>

    <button *ngIf="!link"
      class="LinkElement"
    >
      <ng-container [ngTemplateOutlet]="content"></ng-container>
    </button>

    <ng-template #content>
      <ng-content></ng-content>
    </ng-template>
  `,
  styleUrls: ['./link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkComponent implements OnChanges {
  @Input() link?: string;
  @Input() queryParams: { [k: string]: any };
  @Input() rel = '';
  @HostBinding('attr.data-underline')
  @Input()
  underline: LinkUnderline = 'hover';
  @HostBinding('attr.data-color')
  @Input()
  color?: LinkColor = 'default';
  @HostBinding('attr.data-hover-color')
  @Input()
  hoverColor?: LinkColor = 'default';
  @HostBinding('attr.data-weight')
  @Input()
  weight?: LinkWeight = 'inherit';
  @Input() external?: boolean;

  linkTarget: LinkTarget;
  noopener = false;
  allRel: string;

  isExternalLink(link: string, external: boolean) {
    // Check if the link is http(s) or protocol relative.
    return link.startsWith('http') || link.startsWith('//') || external;
  }

  getLinkTarget(link: string, external: boolean) {
    return this.isExternalLink(link, external) ? '_blank' : undefined;
  }

  ngOnChanges(changes: SimpleChanges) {
    const properties = Object.keys(changes);

    if (properties.includes('link') || properties.includes('external')) {
      const link = properties.includes('link')
        ? changes.link.currentValue
        : this.link;

      const external = properties.includes('external')
        ? changes.external.currentValue
        : this.external;

      this.linkTarget = this.getLinkTarget(link, external);
    }

    if (
      properties.includes('link') ||
      properties.includes('rel') ||
      properties.includes('external')
    ) {
      const link = properties.includes('link')
        ? changes.link.currentValue
        : this.link;

      const rel = properties.includes('rel')
        ? changes.rel.currentValue
        : this.rel;

      const external = properties.includes('external')
        ? changes.external.currentValue
        : this.external;

      // Check if it's an external link and add rel="noopener" as
      // necessary because security exploits.
      // Ref: https://mathiasbynens.github.io/rel-noopener/
      this.noopener = this.isExternalLink(link, external);

      const relArray = rel.split(' ');
      if (this.noopener) {
        relArray.push('noopener');
      }

      this.allRel = relArray.join(' ').trim();
    }
  }
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-i18n-webapp-compat/LANG/es/webapp/src/app/@shared/ui/link/link.component.ts